import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { structureColorEditSessionState } from '~/state/atoms/structureSet/roiAtoms';

/**
 * Draws the structure whose colour is being edited in its unsaved colour,
 * in the Environment that draws the edited structure set (the active edit
 * target's), so the viewer previews an edit before it is applied, and clears that
 * preview when the edit closes or moves to another structure. Repaints
 * are the engine's: at most one per animation frame however fast a drag
 * changes the colour.
 */
export function useStructureColorViewerPreview(): void {
    const session = useRecoilValue(structureColorEditSessionState);
    const { environment } = useActiveEditTarget();
    const structureSetId = session?.structureSetId;
    const roiNumber = session?.roiNumber ?? -1;
    const pending = session?.pending ?? null;

    useEffect(() => {
        // ROI numbers repeat across sets: never preview on a structure of
        // a set the engine has moved on to.
        const engineSet = environment?.structureSet;
        if (engineSet?.parent.id === structureSetId) {
            engineSet?.getRoi(roiNumber)?.setDisplayColorOverride(pending);
        }
    }, [environment, structureSetId, roiNumber, pending]);

    // The engine replaces its structure objects in place when another set
    // is brought up, so the object previewed may no longer be the one
    // drawn: clear both the one there at open and the one there now.
    useEffect(() => {
        const roiAtOpen = environment?.structureSet?.getRoi(roiNumber);
        return () => {
            roiAtOpen?.setDisplayColorOverride(null);
            environment?.structureSet
                ?.getRoi(roiNumber)
                ?.setDisplayColorOverride(null);
        };
    }, [environment, structureSetId, roiNumber]);
}
